import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  apiRequest,
  getAuthToken,
  setAuthToken,
  parseJwt,
} from "@client/api/client";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";

export const Route = createFileRoute("/change-password")({
  component: ChangePasswordPage,
});

function ChangePasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate({ to: "/auth", replace: true });
      return;
    }
    const payload = parseJwt(token);
    if (!payload?.passwordResetRequired) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await apiRequest("/api/staff/change-password", {
        method: "POST",
        body: { newPassword: password },
      });
      toast.success("Password changed successfully.");

      // Need to re-login or update token? The server doesn't issue a new token.
      // Easiest is to log them out and force a re-login so they get a fresh token
      // with passwordResetRequired = false.
      setAuthToken(null);
      window.dispatchEvent(new Event("auth-changed"));
      navigate({ to: "/auth", replace: true });
      toast.info("Please sign in again with your new password.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to change password.",
      );
    } finally {
      setLoading(false);
    }
  }

  function onLogout() {
    setAuthToken(null);
    window.dispatchEvent(new Event("auth-changed"));
    navigate({ to: "/auth", replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary px-4 py-12">
      <div className="w-full max-w-md rounded-lg bg-card p-8 shadow-lg">
        <div className="flex flex-col items-center justify-center space-y-2">
          <h1 className="hall-heading text-2xl font-bold">
            Change Password Required
          </h1>
        </div>
        <p className="mt-1 text-sm text-center text-muted-foreground">
          You are logging in with a temporary password. You must change your
          password before continuing to the portal.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Changing…" : "Change Password"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={loading}
            onClick={onLogout}
          >
            Cancel and Logout
          </Button>
        </form>
      </div>
    </div>
  );
}
